import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import {
  Button,
  ConfirmationModal,
  Icon,
  List,
} from '@folio/stripes-components';
import { CalloutContext, useOkapiKy } from '@folio/stripes/core';
import LanguagesForm from './LanguagesForm';
import { getLocaleLabel } from '../../utils/utils';

function LanguagesManager(props) {
  const [languagesList, setLanguagesList] = useState([]);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [deletedLanguages, setDeletedLanguages] = useState([]);

  const intl = useIntl();
  const okapiKy = useOkapiKy();
  const callout = useContext(CalloutContext);

  const { data, refetch } = useQuery(
    'languages-settings',
    () => okapiKy.get('languages?limit=1000').json(),
    { refetchOnWindowFocus: false },
  );

  useEffect(() => {
    if (data?.languages) {
      setLanguagesList(
        [...data.languages].sort((a, b) =>
          a.localeValue.localeCompare(b.localeValue))
      );
    }
  }, [data]);

  const showSuccess = (message) => {
    callout?.sendCallout({ type: 'success', message });
  };

  const showError = (message) => {
    callout?.sendCallout({ type: 'error', message });
  };

  const onCreateNewLanguages = async (newLanguages) => {
    if (!newLanguages.length) return;

    const results = await Promise.allSettled(
      newLanguages.map((lang) =>
        okapiKy.post('languages/setup', {
          json: {
            name: lang.name,
            localeValue: lang.localeValue,
            status: lang.status || 'active',
          },
        }).json()
      )
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled');
    const failed = results.filter((r) => r.status === 'rejected');

    if (succeeded.length) {
      await refetch();
      showSuccess(
        <div>
          <strong>
            <FormattedMessage
              id="ui-translations.settings.languages.callout.message.addNew"
            />
          </strong>
          <List
            listStyle="bullets"
            items={succeeded.map((_, i) => newLanguages[i])}
            itemFormatter={(item) => (
              <li>{getLocaleLabel(item.localeValue, intl)}</li>
            )}
          />
        </div>
      );
    }

    if (failed.length) {
      failed.forEach(async (result, i) => {
        let errorMessage = 'Unknown error';
        try {
          const err = await result.reason.response?.json();
          errorMessage = err?.errors?.[0]?.message || errorMessage;
        } catch (_) { /* ignore */ }
        showError(
          <FormattedMessage
            id="ui-translations.settings.languages.callout.error.addFailed"
            defaultMessage="Failed to add {locale}: {error}"
            values={{
              locale: getLocaleLabel(newLanguages[i].localeValue, intl),
              error: errorMessage,
            }}
          />
        );
      });
    }
  };

  const onConfirmDeleteLanguages = async () => {
    setOpenConfirmDeleteModal(false);

    const results = await Promise.allSettled(
      deletedLanguages.map((lang) =>
        okapiKy.delete(`languages/cascade/${lang.id}`)
      )
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled');
    const failed = results.filter((r) => r.status === 'rejected');

    if (succeeded.length) {
      await refetch();
      showSuccess(
        <div>
          <strong>
            <FormattedMessage
              id="ui-translations.settings.languages.callout.deletedMessage"
            />
          </strong>
          <List
            listStyle="bullets"
            items={succeeded.map((_, i) => deletedLanguages[i])}
            itemFormatter={(item) => (
              <li>{getLocaleLabel(item.localeValue, intl)}</li>
            )}
          />
        </div>
      );
    }

    if (failed.length) {
      failed.forEach(async (result, i) => {
        let errorMessage = 'Unknown error';
        try {
          const err = await result.reason.response?.json();
          errorMessage = err?.errors?.[0]?.message || errorMessage;
        } catch (_) { /* ignore */ }
        showError(
          <FormattedMessage
            id="ui-translations.settings.languages.callout.error.deleteFailed"
            defaultMessage="Failed to delete {locale}: {error}"
            values={{
              locale: getLocaleLabel(deletedLanguages[i].localeValue, intl),
              error: errorMessage,
            }}
          />
        );
      });
    }
  };

  const onFormSubmit = async (values) => {
    const newLanguages = values.languagesList.filter(
      (lang) => !languagesList.some((l) => l.localeValue === lang.localeValue)
    );
    const removedLanguages = languagesList.filter(
      (lang) => !values.languagesList.some((l) => l.localeValue === lang.localeValue)
    );
    // languages whose status changed
    const updatedLanguages = values.languagesList.filter((lang) => {
      const existing = languagesList.find((l) => l.localeValue === lang.localeValue);
      return existing && existing.status !== lang.status;
    });

    if (newLanguages.length) {
      onCreateNewLanguages(newLanguages);
    }

    if (updatedLanguages.length) {
      await Promise.allSettled(
        updatedLanguages.map((lang) =>
          okapiKy.put(`languages/${lang.id}`, { json: lang }).json()
        )
      );
      await refetch();
    }

    if (removedLanguages.length) {
      setDeletedLanguages(removedLanguages);
      setOpenConfirmDeleteModal(true);
    }
  };

  return (
    <>
      <LanguagesForm
        {...props}
        onSubmit={onFormSubmit}
        initialValues={{ languagesList }}
      />
      <ConfirmationModal
        id="delete-language-confirmation-modal"
        open={openConfirmDeleteModal}
        onConfirm={onConfirmDeleteLanguages}
        onCancel={() => setOpenConfirmDeleteModal(false)}
        confirmLabel={
          <FormattedMessage id="ui-translations.buttons.confirmDelete" />
        }
        heading={
          <Icon icon="exclamation-circle" size="large">
            <FormattedMessage
              id="ui-translations.settings.languages.deleteModal.heading"
            />
          </Icon>
        }
        message={
          <div>
            <FormattedMessage
              id="ui-translations.settings.languages.deleteModal.message.header"
            />
            <List
              listStyle="bullets"
              items={deletedLanguages}
              itemFormatter={(item) => (
                <strong>
                  <li>{getLocaleLabel(item.localeValue, intl)}</li>
                </strong>
              )}
            />
            <FormattedMessage
              id="ui-translations.settings.languages.deleteModal.message"
            />
          </div>
        }
      />
    </>
  );
}

LanguagesManager.propTypes = {};

export default LanguagesManager;
